<script setup lang="ts">
import { ref, nextTick, computed, onBeforeMount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { MenuItemType } from './sidebar'
import { DownIcon } from '@/components/icons'
import {useValueDataStore} from "@/stores";
import {RoleService} from "@/services/RoleService";

interface IProps {
  label: string
  depth: number
  path?: string
  icon?: any
  data?: MenuItemType[]
}

const props = defineProps<IProps>();
const router = useRouter();
const route = useRoute();

const roleService = new RoleService();

const showChildren = ref(false);
const expanded = ref(false);
const containerHeight = ref<string | number>(0);
const container = ref<HTMLElement | null>(null);

const isActive = computed(() => {
  return (
    route.path === props.path ||
    (props.data && props.data.some((child) => route.path === child.path))
  )
})

const toggleMenu = () => {
  expanded.value = !expanded.value;

  if (!showChildren.value) {
    showChildren.value = true;
    nextTick(() => {
      if (container.value) {
        containerHeight.value = `${container.value.scrollHeight}px`;
        setTimeout(() => {
          containerHeight.value = 'fit-content'
          container.value!.style.overflow = 'visible'
        }, 300);
      }
    })
  } else {
    if (container.value) {
      containerHeight.value = `${container.value.scrollHeight}px`;
      container.value.style.overflow = 'hidden';
      setTimeout(() => {
        containerHeight.value = '0px'
      }, 10);
      setTimeout(() => {
        showChildren.value = false
      }, 300);
    }
  }
}

const handleLabelClick = () => {
  if (props.path) {
    router.push(props.path);
  } else {
    toggleMenu();
  }
}

onBeforeMount(() => {
  toggleMenu();
});

const items = computed(() => {
  return props.data?.filter(item => !item.role || roleService.hasRoleForMenuItem(item)) || [];
});

</script>

<template>
  <div class="menu-item" :class="{ expanded: expanded, active: isActive, inactive: !isActive }">
    <div class="label" :style="{ paddingLeft: `${depth * 1.875}rem` }" :class="{ parent: depth === 0 }">
      <div class="left" @click="handleLabelClick" :class="{ bold: depth === 0 }">
        <component class="icon" :is="props.icon" />
        <span>{{ props.label }}</span>
      </div>
      <div v-if="items.length" class="right" @click.stop="toggleMenu">
        <DownIcon class="expand" :class="{ expanded: expanded }" />
      </div>
    </div>
    <div
      v-show="showChildren"
      class="items-container"
      ref="container"
      :style="{ height: containerHeight }"
    >
      <menu-item
        v-for="(item, index) in items"
        :key="index"
        :label="item.label"
        :path="item.path"
        :icon="item.icon"
        :depth="depth + 1"
        :data="item.children"
        :class="{ 'selected-child': route.path === item.path }"
      />
    </div>
  </div>
</template>

<style scoped>
.menu-item {
  position: relative;
  width: 100%;
}

.menu-item .label:hover {
  cursor: pointer;
  background-color: var(--color-background);
}

.menu-item .label {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  white-space: nowrap;
  user-select: none;
  height: 2.1875rem;
  padding: 0 0.9375rem 0 0;
  box-sizing: border-box;
  color: var(--color-text);
  transition: all 0.3s ease;
}

.menu-item .label.parent {
  height: 3.125rem;
}

.menu-item .label > div {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.menu-item .label .left {
  font-size: var(--font-size-body);
}

.menu-item .label .left .icon {
  font-size: var(--font-size-menu-icon);
}

.menu-item .label .left.bold {
  font-weight: 600;
  font-size: var(--font-size-h1);
}

.menu-item .label .expand {
  font-size: var(--font-size-body);
  color: var(--color-text);
}

.menu-item .label .expand.expanded {
  transform: rotate(180deg);
}

.menu-item .items-container {
  width: 100%;
  overflow: hidden;
  transition: height 0.3s ease;
}

.menu-item.active .label {
  opacity: 1 !important;
}

.menu-item.inactive .label {
  opacity: 0.35;
}

.menu-item .selected-child .label .left span {
  color: var(--vt-c-tertiary);
  font-weight: 600;
}
</style>
